/**
 * Due-date presentation for board cards.
 *
 * `dueDate` has been on the card DTO all along and was rendered only in the issue drawer and the
 * roadmap — so the one place a date actually changes what you pick up next, the board, never
 * showed it. The formatting is pinned to `en-US` on purpose: this is a compact chip whose width
 * the column layout depends on, and the board's other date text (the burndown axis) already
 * reads that way, so a locale-shifted month name would be both wider and inconsistent.
 */

/** Wire format is a plain `YYYY-MM-DD` calendar day — parse it as LOCAL midnight, never UTC. */
function parseCalendarDay(iso: string): Date | null {
  const match: RegExpMatchArray | null = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  const date: Date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(date.getTime()) ? null : date;
}

/** `Aug 26`, or the year too when it is not the current one. */
export function formatDueDate(iso: string | null | undefined, now: Date = new Date()): string | null {
  if (!iso) return null;
  const date: Date | null = parseCalendarDay(iso);
  if (!date) return null;
  const sameYear: boolean = date.getFullYear() === now.getFullYear();
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" })
  });
}

/**
 * Past due — compared by CALENDAR DAY, so a task due today is never red, whatever the clock says.
 * A card in a done column is never overdue: the work landed, and painting its date red would make
 * a finished column look like a problem.
 */
export function isOverdue(iso: string | null | undefined, isDone: boolean, now: Date = new Date()): boolean {
  if (!iso || isDone) return false;
  const date: Date | null = parseCalendarDay(iso);
  if (!date) return false;
  const today: Date = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return date.getTime() < today.getTime();
}
